import { __assign } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useMergeRefs } from "../../internal/hooks/use-merge-refs";
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import OptionsList from "../../internal/components/options-list";
import { renderOptions } from "../utils/render-options";
import { useVirtual } from "react-virtual";
import { useContainerQuery } from "../../internal/hooks/container-queries";
import styles from "./styles.css.js";
var VirtualList = function(props, ref) {
    return props.menuProps.open ? /*#__PURE__*/ React.createElement(VirtualListOpen, __assign({}, props, {
        ref: ref
    })) : /*#__PURE__*/ React.createElement(VirtualListClosed, __assign({}, props, {
        ref: ref
    }));
};
var VirtualListOpen = /*#__PURE__*/ forwardRef(function(_a, ref) {
    var menuProps = _a.menuProps, getOptionProps = _a.getOptionProps, filteredOptions = _a.filteredOptions, filteringValue = _a.filteringValue, highlightType = _a.highlightType, checkboxes = _a.checkboxes, hasDropdownStatus = _a.hasDropdownStatus, listBottom = _a.listBottom, useInteractiveGroups = _a.useInteractiveGroups, screenReaderContent = _a.screenReaderContent;
    // update component, when it gets wider or narrower to reposition items
    var _b = useContainerQuery(function(rect) {
        return rect.width;
    }, []), width = _b[0], menuMeasureRef = _b[1];
    var menuRefObject = useRef(null);
    var menuRef = useMergeRefs(menuMeasureRef, menuRefObject, menuProps.ref);
    var _c = useVirtual({
        size: filteredOptions.length,
        parentRef: menuRefObject,
        // estimateSize is a dependency of measurements memo. We update it to force full recalculation
        // when the height of any option could have changed:
        // 1: because the component got resized (width property got updated)
        // 2: because the option changed its content (filteringValue property controls the highlight and the visibility of hidden tags)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        estimateSize: useCallback(function() {
            return 31;
        }, [
            width,
            filteringValue
        ]),
        overscan: 5
    }), virtualItems = _c.virtualItems, totalSize = _c.totalSize, scrollToIndex = _c.scrollToIndex;
    useImperativeHandle(ref, function() {
        return function(index) {
            if (highlightType === "keyboard") {
                scrollToIndex(index);
            }
        };
    }, [
        highlightType,
        scrollToIndex
    ]);
    var finalOptions = renderOptions({
        options: virtualItems.map(function(_a) {
            var index = _a.index;
            return filteredOptions[index];
        }),
        getOptionProps: getOptionProps,
        filteringValue: filteringValue,
        highlightType: highlightType,
        checkboxes: checkboxes,
        hasDropdownStatus: hasDropdownStatus,
        virtualItems: virtualItems,
        useInteractiveGroups: useInteractiveGroups,
        screenReaderContent: screenReaderContent,
        ariaSetsize: filteredOptions.length
    });
    return /*#__PURE__*/ React.createElement(OptionsList, __assign({}, menuProps, {
        ref: menuRef
    }), /*#__PURE__*/ React.createElement("div", {
        "aria-hidden": "true",
        key: "total-size",
        className: styles["layout-strut"],
        style: {
            height: totalSize
        }
    }), finalOptions, listBottom ? /*#__PURE__*/ React.createElement("li", {
        role: "option",
        className: styles["list-bottom"]
    }, listBottom) : null);
});
var VirtualListClosed = /*#__PURE__*/ forwardRef(function(_a, ref) {
    var menuProps = _a.menuProps, listBottom = _a.listBottom;
    useImperativeHandle(ref, function() {
        return function() {};
    }, []);
    return /*#__PURE__*/ React.createElement(OptionsList, __assign({}, menuProps, {
        ref: menuProps.ref
    }), listBottom ? /*#__PURE__*/ React.createElement("li", {
        role: "option",
        className: styles["list-bottom"]
    }, listBottom) : null);
});
export default /*#__PURE__*/ forwardRef(VirtualList); //# sourceMappingURL=virtual-list.js.map
